import type { inferRouterOutputs } from '@trpc/server'
import { useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { Snackbar, Switch } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'

import { AppRouter } from '@rescuebase/api/client-types'

import ReportPreferencesBottomSheet, {
  ReportPreferencesBottomSheetHandle,
} from '@/components/organization/cards/ReportPreferencesBottomSheet'
import Card from '@/components/shared/Card'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import { useAppStore } from '@/store/useAppStore'
import trpc from '@/utils/trpc'

const REPORT_TYPE = 'AdoptionWeekly' as const
type RouterOutput = inferRouterOutputs<AppRouter>
type Pref = RouterOutput['organization']['reportPreferencesList'][number]

interface Props {
  disabled?: boolean
}

const OrganizationReportsCard = (props: Props) => {
  const { disabled = false } = props || ({} as Props)
  const currentOrganization = useAppStore.use.currentOrganization().organization

  const organizationId = currentOrganization?.id || ''

  const listQuery = trpc.organization.reportPreferencesList.useQuery(
    { organizationId },
    { enabled: !!organizationId }
  )
  const usersQuery = trpc.user.list.useQuery(
    { organizationId },
    { enabled: !!organizationId }
  )

  const updateReportPreferences =
    trpc.organization.updateReportPreferences.useMutation()

  const [showSaved, setShowSaved] = useState(false)

  const reports = useMemo(
    () => [
      {
        type: REPORT_TYPE,
        title: 'Weekly Adoption Report',
        description: 'A concise adoption summary delivered every Sunday.',
      },
    ],
    []
  )

  const isSaving = updateReportPreferences.isPending
  const isBusy = isSaving || listQuery.isPending || usersQuery.isPending

  const userList = usersQuery.data || []

  const preferenceMap = useMemo(() => {
    const map = new Map<Pref['reportType'], Pref>()
    ;(listQuery.data || []).forEach((p) =>
      map.set(p.reportType as Pref['reportType'], p as Pref)
    )
    return map
  }, [listQuery.data])

  const sheetRef = useRef<ReportPreferencesBottomSheetHandle>(null)

  const openManage = (reportType: Pref['reportType']) => {
    const pref = preferenceMap.get(reportType)
    sheetRef.current?.present({
      title: reports.find((r) => r.type === reportType)?.title || 'Recipients',
      description:
        reports.find((r) => r.type === reportType)?.description || undefined,
      users: userList,
      initial: {
        recipientUserIds: pref?.recipientUserIds || [],
      },
      onSave: (prefs) => {
        if (!organizationId || !reportType) return
        updateReportPreferences.mutate(
          {
            organizationId,
            reportType: reportType,
            isEnabled: preferenceMap.get(reportType)?.isEnabled ?? false,
            recipientUserIds: prefs.recipientUserIds,
          },
          {
            onSuccess: async () => {
              setShowSaved(true)
              await listQuery.refetch()
            },
          }
        )
      },
    })
  }

  const handleToggleEnabled = (
    reportType: Pref['reportType'],
    value: boolean
  ) => {
    if (!organizationId) return
    const pref = preferenceMap.get(reportType)
    updateReportPreferences.mutate({
      organizationId,
      reportType: reportType,
      isEnabled: value,
      recipientUserIds: pref?.recipientUserIds || [],
    })
  }

  return (
    <Card title="Reports">
      <View style={styles.section}>
        {reports.map((r) => {
          const pref = preferenceMap.get(r.type as Pref['reportType'])
          const summary = `${pref?.recipientUserIds?.length || 0} selected`
          return (
            <View key={r.type} style={styles.reportRow}>
              <View style={styles.reportInfo}>
                <UniText variant="titleSmall">{r.title}</UniText>
                <UniText style={styles.subtitle} variant="bodySmall">
                  {r.description}
                </UniText>
                <View style={styles.summaryRow}>
                  <UniText style={styles.summaryLabel}>Recipients:</UniText>
                  <UniText>{summary}</UniText>
                </View>
              </View>
              <View style={styles.reportActions}>
                <Switch
                  disabled={disabled || isBusy}
                  onValueChange={(v) =>
                    handleToggleEnabled(r.type as Pref['reportType'], v)
                  }
                  value={!!pref?.isEnabled}
                />
                <UniButton
                  disabled={disabled || isBusy}
                  mode="outlined"
                  onPress={() => openManage(r.type)}
                  style={styles.manageButton}
                >
                  Manage
                </UniButton>
              </View>
            </View>
          )
        })}

        <Snackbar
          duration={2000}
          onDismiss={() => setShowSaved(false)}
          visible={showSaved}
        >
          <UniText>Preferences saved</UniText>
        </Snackbar>
      </View>

      <ReportPreferencesBottomSheet ref={sheetRef} />
    </Card>
  )
}

const styles = StyleSheet.create((theme) => ({
  manageButton: {
    alignSelf: 'flex-end',
  },
  reportActions: {
    alignItems: 'flex-end',
    gap: theme.tokens.spacing[1],
  },
  reportInfo: {
    flex: 1,
    paddingRight: theme.tokens.spacing[2],
  },
  reportRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.tokens.spacing[2],
  },
  section: {
    gap: theme.tokens.spacing[2],
  },
  subtitle: {
    color: theme.colors.subtitle,
    marginTop: theme.tokens.spacing[1],
  },
  summaryLabel: {
    color: theme.colors.subtitle,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: theme.tokens.spacing[1],
    marginTop: theme.tokens.spacing[1],
  },
}))

export default OrganizationReportsCard
